import React, { useState, useEffect } from "react";
import {
  Settings,
  User,
  Shield,
  Database,
  Eye,
  EyeOff,
  Save,
  RefreshCw,
  Key,
  LogOut,
  CheckCircle,
  AlertCircle,
  X,
} from "lucide-react";
import { LoginUserData } from "../LoginForm/LoginForm";
import { realAPI, OrganizationSettingsDTO } from "../../services/realAPI";

interface AdminSettingsProps {
  currentUser: LoginUserData;
  onLogout: () => void;
}

interface PasswordChangeForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const AdminSettings: React.FC<AdminSettingsProps> = ({
  currentUser,
  onLogout,
}) => {
  const [activeSection, setActiveSection] = useState("profile");
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");

  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState<PasswordChangeForm>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Organization settings state
  const [orgSettings, setOrgSettings] = useState<OrganizationSettingsDTO>({
    name: currentUser.organization?.name || "",
    description: "",
    website: "",
    timezone: "UTC",
    maxTeamSize: 20,
    sessionTimeoutMinutes: 60,
    slackIntegrationEnabled: false,
    jiraIntegrationEnabled: false,
  });

  // New status input for workflow statuses
  const [newStatusInput, setNewStatusInput] = useState("");

  const [profileSettings, setProfileSettings] = useState({
    firstName: currentUser.firstName || "",
    lastName: currentUser.lastName || "",
    email: currentUser.email || "",
    jobTitle: currentUser.jobTitle || "",
    phone: "",
    avatar: "",
  });

  useEffect(() => {
    loadOrganizationSettings();
  }, []);

  const loadOrganizationSettings = async () => {
    try {
      setIsLoading(true);
      const settings = await realAPI.getOrganizationSettings();
      setOrgSettings(settings);
    } catch (error) {
      console.error("Error loading organization settings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaveStatus("saving");

      // Only save organization settings for now
      // Profile settings would need a separate endpoint
      if (
        activeSection === "organization" ||
        activeSection === "integrations"
      ) {
        await realAPI.updateOrganizationSettings(orgSettings);
      }

      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 3000);
    } catch (error) {
      console.error("Error saving settings:", error);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError("");

    // Validation
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      return;
    }

    try {
      await realAPI.users.changeOwnPassword(
        passwordForm.currentPassword,
        passwordForm.newPassword
      );

      // Success - close modal and reset form
      setShowPasswordModal(false);
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setPasswordError("");

      // Show message and redirect to login
      alert(
        "Password changed successfully. You will be redirected to login with your new password."
      );

      // Use the logout function to properly clear all tokens and data
      await realAPI.logout();

      // Redirect to login page after a short delay
      setTimeout(() => {
        window.location.href = "/login";
      }, 2000);
    } catch (error) {
      console.error("Error changing password:", error);
      setPasswordError("Failed to change password. Please try again.");
    }
  };

  const handleLogout = async () => {
    if (window.confirm("Are you sure you want to logout?")) {
      try {
        await realAPI.logout();
        onLogout();
      } catch (error) {
        console.error("Logout error:", error);
        onLogout();
      }
    }
  };

  const settingsSections = [
    { id: "profile", label: "Profile", icon: User },
    { id: "organization", label: "Organization", icon: Settings },
    { id: "security", label: "Security", icon: Shield },
    // { id: "integrations", label: "Integrations", icon: Database },
  ];

  const renderProfileSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Profile Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              First Name
            </label>
            <input
              type="text"
              value={profileSettings.firstName}
              onChange={(e) =>
                setProfileSettings({
                  ...profileSettings,
                  firstName: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Last Name
            </label>
            <input
              type="text"
              value={profileSettings.lastName}
              onChange={(e) =>
                setProfileSettings({
                  ...profileSettings,
                  lastName: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={profileSettings.email}
              onChange={(e) =>
                setProfileSettings({
                  ...profileSettings,
                  email: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Job Title
            </label>
            <input
              type="text"
              value={profileSettings.jobTitle}
              onChange={(e) =>
                setProfileSettings({
                  ...profileSettings,
                  jobTitle: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Account Actions
        </h3>
        <div className="space-y-4">
          <button
            onClick={handleLogout}
            className="flex items-center px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors border border-red-300 hover:border-red-400"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </button>
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Password Security
        </h3>

        {/* Change Password Button */}
        <div className="mb-6">
          <button
            onClick={() => setShowPasswordModal(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors border border-blue-300 hover:border-blue-400"
          >
            <Key className="h-4 w-4 mr-2" />
            Change Password
          </button>
        </div>

        {/* Password Requirements */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <Shield className="h-5 w-5 text-blue-400 mt-0.5" />
            <div className="ml-3">
              <h4 className="text-sm font-medium text-blue-800">
                Password Requirements
              </h4>
              <div className="text-sm text-blue-700 mt-1">
                <ul className="list-disc list-inside space-y-1">
                  <li>Minimum 8 characters</li>
                  <li>Must contain uppercase and lowercase letters</li>
                  <li>Must contain at least one number</li>
                  <li>Must contain at least one special character</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderOrganizationSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Organization Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Organization Name
            </label>
            <input
              type="text"
              value={orgSettings.name}
              onChange={(e) =>
                setOrgSettings({ ...orgSettings, name: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={orgSettings.description}
              onChange={(e) =>
                setOrgSettings({ ...orgSettings, description: e.target.value })
              }
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Website
            </label>
            <input
              type="url"
              value={orgSettings.website}
              onChange={(e) =>
                setOrgSettings({ ...orgSettings, website: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Timezone
            </label>
            <select
              value={orgSettings.timezone}
              onChange={(e) =>
                setOrgSettings({ ...orgSettings, timezone: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="UTC">UTC</option>
              <option value="America/New_York">Eastern Time</option>
              <option value="America/Chicago">Central Time</option>
              <option value="America/Denver">Mountain Time</option>
              <option value="America/Los_Angeles">Pacific Time</option>
            </select>
          </div>
        </div>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Team Settings
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Maximum Team Size
            </label>
            <input
              type="number"
              value={orgSettings.maxTeamSize}
              onChange={(e) =>
                setOrgSettings({
                  ...orgSettings,
                  maxTeamSize: parseInt(e.target.value),
                })
              }
              min="1"
              max="100"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Session Timeout (minutes)
            </label>
            <input
              type="number"
              value={orgSettings.sessionTimeoutMinutes}
              onChange={(e) =>
                setOrgSettings({
                  ...orgSettings,
                  sessionTimeoutMinutes: parseInt(e.target.value),
                })
              }
              min="15"
              max="480"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
      <div className="border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Workflow Statuses
        </h3>
        <p className="text-sm text-gray-500 mb-3">Configure the columns/statuses for your organization's boards. Example: todo, inProgress, deployed, done</p>

        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <input
              type="text"
              placeholder="Add status (e.g. todo)"
              value={newStatusInput}
              onChange={(e) => setNewStatusInput(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1"
            />
            <button
              onClick={() => {
                const value = newStatusInput.trim();
                if (!value) return;
                const existing = orgSettings.workflowStatuses || [];
                if (existing.includes(value as any)) {
                  setNewStatusInput("");
                  return;
                }
                setOrgSettings({ ...orgSettings, workflowStatuses: [...existing, value as any] });
                setNewStatusInput("");
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Add
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {(orgSettings.workflowStatuses || ['todo','inProgress','deployed','done']).map((s, idx) => (
              <div key={`${s}-${idx}`} className="flex items-center px-3 py-1 bg-gray-100 rounded-full text-sm">
                <span className="mr-2">{s}</span>
                <button
                  onClick={() => {
                    const list = (orgSettings.workflowStatuses || ['todo','inProgress','deployed','done']).filter((x) => x !== s);
                    setOrgSettings({ ...orgSettings, workflowStatuses: list as any });
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderIntegrationsSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Third-party Integrations
        </h3>
        <div className="space-y-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Database className="h-5 w-5 text-purple-600" />
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-gray-900">
                    Slack Integration
                  </h4>
                  <p className="text-sm text-gray-500">
                    Get notifications in Slack
                  </p>
                </div>
              </div>
              <button
                onClick={() =>
                  setOrgSettings({
                    ...orgSettings,
                    slackIntegrationEnabled:
                      !orgSettings.slackIntegrationEnabled,
                  })
                }
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  orgSettings.slackIntegrationEnabled
                    ? "bg-red-100 text-red-700 hover:bg-red-200"
                    : "bg-green-100 text-green-700 hover:bg-green-200"
                }`}
              >
                {orgSettings.slackIntegrationEnabled ? "Disconnect" : "Connect"}
              </button>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Database className="h-5 w-5 text-blue-600" />
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-gray-900">
                    Jira Integration
                  </h4>
                  <p className="text-sm text-gray-500">Sync tasks with Jira</p>
                </div>
              </div>
              <button
                onClick={() =>
                  setOrgSettings({
                    ...orgSettings,
                    jiraIntegrationEnabled: !orgSettings.jiraIntegrationEnabled,
                  })
                }
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  orgSettings.jiraIntegrationEnabled
                    ? "bg-red-100 text-red-700 hover:bg-red-200"
                    : "bg-green-100 text-green-700 hover:bg-green-200"
                }`}
              >
                {orgSettings.jiraIntegrationEnabled ? "Disconnect" : "Connect"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case "profile":
        return renderProfileSettings();
      case "organization":
        return renderOrganizationSettings();
      case "security":
        return renderSecuritySettings();
      case "integrations":
        return renderIntegrationsSettings();
      default:
        return renderProfileSettings();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
          <p className="text-gray-600">
            Manage your account and organization preferences
          </p>
        </div>
        <button
          onClick={handleSaveSettings}
          disabled={saveStatus === "saving"}
          className={`flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            saveStatus === "saved"
              ? "bg-green-100 text-green-700 border border-green-300"
              : saveStatus === "error"
              ? "bg-red-100 text-red-700 border border-red-300"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          {saveStatus === "saving" ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : saveStatus === "saved" ? (
            <CheckCircle className="h-4 w-4 mr-2" />
          ) : saveStatus === "error" ? (
            <AlertCircle className="h-4 w-4 mr-2" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {saveStatus === "saving"
            ? "Saving..."
            : saveStatus === "saved"
            ? "Saved!"
            : saveStatus === "error"
            ? "Error"
            : "Save Changes"}
        </button>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="flex border-b border-gray-200">
          {/* Sidebar Navigation */}
          <div className="w-64 p-4">
            <nav className="space-y-1">
              {settingsSections.map((section) => {
                const IconComponent = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      activeSection === section.id
                        ? "bg-blue-50 text-blue-700 border-r-2 border-blue-700"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    <IconComponent className="h-4 w-4 mr-3" />
                    {section.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : (
              renderContent()
            )}
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Change Password
              </h3>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {passwordError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{passwordError}</p>
              </div>
            )}

            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? "text" : "password"}
                    required
                    value={passwordForm.currentPassword}
                    onChange={(e) =>
                      setPasswordForm({
                        ...passwordForm,
                        currentPassword: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowPasswords({
                        ...showPasswords,
                        current: !showPasswords.current,
                      })
                    }
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.current ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? "text" : "password"}
                    required
                    value={passwordForm.newPassword}
                    onChange={(e) =>
                      setPasswordForm({
                        ...passwordForm,
                        newPassword: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowPasswords({
                        ...showPasswords,
                        new: !showPasswords.new,
                      })
                    }
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.new ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? "text" : "password"}
                    required
                    value={passwordForm.confirmPassword}
                    onChange={(e) =>
                      setPasswordForm({
                        ...passwordForm,
                        confirmPassword: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowPasswords({
                        ...showPasswords,
                        confirm: !showPasswords.confirm,
                      })
                    }
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.confirm ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;
